<script lang="ts" setup>
import { useMateTrait } from "@/stores/mateTrait";
import { onBeforeMount, ref } from "vue";
import { onCurrentMateChange } from "@/stores/internal-subscribe";
import { useRouter } from "vue-router";
import CheckCircleFillIcon from "@/components/icon/CheckCircleFillIcon.vue";
import UserAvatar from "@/components/UserAvatar.vue";
import { showConfirmDialog, showDialog, showLoadingToast } from "vant";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
// Props
const props = withDefaults(
  defineProps<{
    selectedTraitId?: number | null;
    selectedTrait?: MateTrait | null;
    enableSelection?: boolean; // 启用选择模式
    enableDelete?: boolean;
    enableEdit?: boolean;
    enableSelectionLock?: boolean; // 选择模式下，启用锁定selectedTraitId(不允许切换特征)
    enableSelectionOptional?: boolean; // 选择模式下，是否可以不选特征
    hidePresetStyle?: boolean; // 隐藏预设风格
  }>(),
  {
    enableSelection: false,
    enableDelete: false,
    enableLock: false,
    enableSelectionLock: false,
    enableEdit: false,
    enableSelectionOptional: true,
    hidePresetStyle: false,
  }
);
const { t } = useI18n();

const traitId = computed(
  () => props.selectedTraitId ?? props.selectedTrait?.id ?? null
);

const emit = defineEmits<{
  (e: "update:selectedTrait", trait: MateTrait | null): void;
  (e: "update:selectedTraitId", traitId: number | null): void;
  (e: "update:showCreateTraitModal", show: boolean): void;
}>();

// State
const router = useRouter();
const mateTrait = useMateTrait();
const loading = ref(false);

const traitList = computed(() =>
  props.hidePresetStyle
    ? mateTrait.traitList.filter((trait) => trait.customStyle?.version)
    : mateTrait.traitList
);

// Lifecycle
const prepareData = async (forceReload = false) => {
  loading.value = true;
  try {
    await mateTrait.getOrFetchTraitList(forceReload);
    if (!props.enableSelectionOptional && !traitId.value) {
      emit("update:selectedTrait", mateTrait.traitList[0]);
      emit("update:selectedTraitId", mateTrait.traitList[0].id!);
    }
  } finally {
    loading.value = false;
  }
};

onBeforeMount(() => {
  prepareData();
});

onCurrentMateChange(() => {
  prepareData(true);
});

// EventHandler
const onTraitSelect = (trait: MateTrait) => {
  if (!props.enableSelection || props.enableSelectionLock) return;

  // 如果已经选中了，再次点击则取消选中；若启用了锁定，则不取消选中
  if (props.enableSelectionOptional && traitId.value === trait.id) {
    emit("update:selectedTrait", null);
    emit("update:selectedTraitId", null);
  } else {
    emit("update:selectedTrait", trait);
    emit("update:selectedTraitId", trait.id!);
  }
};
const onDelete = async (traitId: number) => {
  if (mateTrait.traitList.length === 1) {
    await showDialog({
      title: t("Message.deleteFeature"),
      message: t("Message.lastFeatureNotDeleted"),
    });
    return;
  }
  try {
    await showConfirmDialog({
      title: t("Message.deleteFeature"),
      message: t("Message.sureRemoveFeature"),
      confirmButtonText: t("Buttons.confirm"),
      cancelButtonText: t("Buttons.cancel"),
    });
  } catch {
    return;
  }
  const loadingToast = showLoadingToast({
    message: t("Message.deleting"),
    duration: 0,
  });
  try {
    await mateTrait.deleteTrait(traitId);
  } finally {
    loadingToast.close();
  }
};

const clickEditTrait = async (mateTrait: MateTrait) => {
  try {
    await showConfirmDialog({
      title: t("Message.editTrait"),
      message: t("Message.editTraitConfirm"),
      confirmButtonText: t("Buttons.confirm"),
      cancelButtonText: t("Buttons.cancel"),
    });
    const { id, mode } = mateTrait;
    router.push({ name: "createTraitTask", query: { id, mode } });
  } catch (e) {}
};
</script>
<template>
  <div class="flex text-xs border p-2 max-w-[calc(100vw-32px)] w-full rounded">
    <div v-if="loading" class="w-full h-20 flex items-center justify-center">
      <span class="loading"></span>
    </div>
    <div
      v-else-if="mateTrait.traitList.length === 0"
      class="text-center w-full"
    >
      <div>{{ $t(`painting.people.noFeatureInfo`) }}</div>
      <div>{{ $t(`painting.people.noFeatureInfoDesc`) }}</div>
      <button
        class="btn btn-xs rounded mt-2"
        @click="emit(`update:showCreateTraitModal`, true)"
      >
        {{ $t("painting.people.create") }}
      </button>
    </div>
    <div
      v-else-if="selectedTrait && enableSelectionLock"
      class="flex gap-2 flex-nowrap overflow-x-auto"
    >
      <div class="flex flex-col w-20 aspect-[3/4] relative shrink-0">
        <div
          class="border-primary relative border-2 rounded-lg overflow-hidden"
        >
          <user-avatar
            :avatar-url="selectedTrait.media?.url || selectedTrait.source?.url"
            class="w-full h-full"
          />
          <check-circle-fill-icon
            class="text-primary icon-base absolute left-1 top-1"
          />
        </div>
        <div class="text-center shrink-0 flex items-center justify-center mb-1">
          <span class="flex-1 line-clamp-1"> {{ selectedTrait.name }}</span>
          <button
            v-if="enableEdit"
            @click.stop="clickEditTrait(selectedTrait)"
            class="btn btn-xs p-0 rounded-full w-6 h-6 max-h-6"
          >
            <i class="iconify mingcute--edit-3-line" />
          </button>
        </div>
        <button
          v-if="enableDelete"
          class="btn btn-xs rounded"
          @click="
            $event.stopPropagation();
            onDelete(selectedTrait.id!);
          "
        >
          {{ $t("Buttons.delete") }}
        </button>
      </div>
    </div>
    <div v-else class="flex gap-2 flex-nowrap overflow-x-auto">
      <div
        v-for="item in traitList"
        v-show="!enableSelectionLock || traitId === item.id"
        :key="item.id"
        class="flex flex-col w-20 aspect-[3/4] relative shrink-0"
        @click="onTraitSelect(item)"
      >
        <div
          :class="{
            'border-primary': traitId === item.id,
            'border-transparent': traitId !== item.id,
          }"
          class="relative border-2 rounded-lg overflow-hidden"
        >
          <user-avatar
            :avatar-url="item.media?.url || item.source?.url"
            class="w-full h-full"
          />
          <check-circle-fill-icon
            v-if="traitId === item.id"
            class="text-primary icon-base absolute left-1 top-1"
          />
        </div>
        <div class="text-center shrink-0 flex items-center justify-center mb-1">
          <span class="flex-1 line-clamp-1"> {{ item.name }}</span>
          <button
            v-if="enableEdit"
            @click.stop="clickEditTrait(item)"
            class="btn btn-xs p-0 rounded-full w-6 h-6 max-h-6"
          >
            <i class="iconify mingcute--edit-3-line" />
          </button>
        </div>
        <button
          v-if="enableDelete"
          class="btn btn-xs rounded"
          @click="
            $event.stopPropagation();
            onDelete(item.id!);
          "
        >
          {{ $t("Buttons.delete") }}
        </button>
      </div>
    </div>
  </div>
</template>
